package com.snakesworld.game.engine

import com.snakesworld.game.network.SessionResult
import kotlinx.serialization.Serializable
import kotlin.math.PI

/**
 * Strict 4-Directional orthogonal movement enum (UP, DOWN, LEFT, RIGHT).
 */
@Serializable
enum class Direction(val dx: Float, val dy: Float, val angle: Float) {
    UP(0f, -1f, (-PI / 2.0).toFloat()),
    DOWN(0f, 1f, (PI / 2.0).toFloat()),
    LEFT(-1f, 0f, PI.toFloat()),
    RIGHT(1f, 0f, 0f);

    fun isOpposite(other: Direction): Boolean {
        return (this == UP && other == DOWN) ||
                (this == DOWN && other == UP) ||
                (this == LEFT && other == RIGHT) ||
                (this == RIGHT && other == LEFT)
    }

    fun toVector(): Vector2D = Vector2D(dx, dy)

    companion object {
        fun fromAngle(angle: Float): Direction {
            var norm = angle % (2.0 * PI).toFloat()
            if (norm < 0) norm += (2.0 * PI).toFloat()
            // 0 is Right, PI/2 is Down, PI is Left, 3PI/2 is Up
            return when {
                norm >= 7 * PI / 4 || norm < PI / 4 -> RIGHT
                norm >= PI / 4 && norm < 3 * PI / 4 -> DOWN
                norm >= 3 * PI / 4 && norm < 5 * PI / 4 -> LEFT
                else -> UP
            }
        }
    }
}

/**
 * Body segment representing a single follower link in the kinematic chain.
 */
@Serializable
data class SnakeSegment(
    val position: Vector2D,
    val angle: Float = 0f,
)

/**
 * Complete in-game snake player state.
 */
@Serializable
data class SnakePlayer(
    val id: String,
    val name: String,
    val skinId: String = "neon_green",
    val headPosition: Vector2D = Vector2D.ZERO,
    val direction: Direction = Direction.RIGHT,
    val segments: List<SnakeSegment> = emptyList(),
    val mass: Float = GameConstants.INITIAL_SNAKE_MASS,
    val speed: Float = GameConstants.BASE_SNAKE_SPEED,
    val isBoosting: Boolean = false,
    val isAlive: Boolean = true,
    val score: Int = 0,
    val kills: Int = 0,
    val joinedAt: Long = 0L,
    val lastHeartbeat: Long = 0L,
)
/**
 * Edible food item in the battle arena (ambient, death orb, or boost trail).
 */
@Serializable
data class FoodPellet(
    val id: String,
    val position: Vector2D,
    val mass: Float = 1f,
    val color: Long = GameConstants.COLOR_NEON_GREEN,
    val isCorpse: Boolean = false,
    val isBoostTrail: Boolean = false,
)

/**
 * Top-level snapshot of the active battle arena.
 */
data class GameState(
    val localPlayer: SnakePlayer? = null,
    val remoteSnakes: Map<String, SnakePlayer> = emptyMap(),
    val pellets: Map<String, FoodPellet> = emptyMap(),
    val leaderboard: List<LeaderboardEntry> = emptyList(),
    val killEvents: List<KillEvent> = emptyList(),
    val isGameOver: Boolean = false,
    val gameOverStats: SessionResult? = null,
    val isPaused: Boolean = false,
)

/**
 * Leaderboard row showing top pilots.
 */
@Serializable
data class LeaderboardEntry(
    val id: String,
    val name: String,
    val score: Int,
    val kills: Int,
    val skinId: String = "neon_green",
)

/**
 * Kill event broadcast when a snake eliminates an opponent.
 */
@Serializable
data class KillEvent(
    val killerId: String,
    val killerName: String,
    val victimId: String,
    val victimName: String,
    val timestamp: Long,
)

/**
 * Customizable snake color option with neon primary & secondary accents.
 */
@Serializable
data class SnakeSkinOption(
    val id: String,
    val name: String,
    val primaryColor: Long,
    val secondaryColor: Long,
)

val SNAKE_SKIN_OPTIONS: List<SnakeSkinOption> = listOf(
    SnakeSkinOption("neon_viper", "NEON GREEN", GameConstants.COLOR_NEON_GREEN, GameConstants.COLOR_NEON_CYAN),
    SnakeSkinOption("cyber_cyan", "CYBER CYAN", GameConstants.COLOR_NEON_CYAN, GameConstants.COLOR_NEON_BLUE),
    SnakeSkinOption("plasma_magenta", "HOT MAGENTA", GameConstants.COLOR_NEON_MAGENTA, GameConstants.COLOR_NEON_YELLOW),
    SnakeSkinOption("solar_yellow", "SOLAR YELLOW", GameConstants.COLOR_NEON_YELLOW, GameConstants.COLOR_NEON_ORANGE),
    SnakeSkinOption("blaze_orange", "BLAZE ORANGE", GameConstants.COLOR_NEON_ORANGE, GameConstants.COLOR_NEON_RED),
    SnakeSkinOption("void_purple", "MATRIX PURPLE", GameConstants.COLOR_NEON_PURPLE, GameConstants.COLOR_NEON_MAGENTA),
    SnakeSkinOption("crimson_red", "CRIMSON RED", GameConstants.COLOR_NEON_RED, GameConstants.COLOR_NEON_ORANGE),
    SnakeSkinOption("arctic_white", "ARCTIC WHITE", 0xFFE2E8F0, 0xFF94A3B8),
)
